import {
  Button,
  Dialog,
  Heading,
  Inline,
  LinkButton,
  Stack,
  Text,
  textVariants,
  vars,
} from '@etg/wings';
import { useTranslation } from '@eti/providers';
import { css } from '@eti/styles';
import { constants, phone } from '@eti/utils';
import { Envelope, Phone } from '@phosphor-icons/react';
import type { Dispatch, SetStateAction } from 'react';
import type { Country, Traveler } from '../../models';
import InfoReviewModalTraveler from './InfoReviewModalTraveler';

const { MOUSE_FLOW_CLASS_NAMES } = constants;

const dividerStyles = css`
  background-color: ${vars.colors.divider};
  height: 1px;
  width: 100%;
`;

const informationWrapperStyles = css`
  align-items: center;
  justify-content: space-between;
`;

const linkButtonStyles = css`
  padding: 2px 0;
`;

interface InfoReviewModalProps {
  contactInfo: {
    email: string;
    phone: string;
    phoneCountry: number;
  };
  countries: Country[];
  handleEditInfo: (scrollTo?: string) => void;
  isOpen: boolean;
  onSubmit: () => void;
  setShowInfoReviewModal: Dispatch<SetStateAction<boolean>>;
  travelers: Traveler[];
}

const InfoReviewModal = ({
  contactInfo,
  countries,
  handleEditInfo,
  isOpen,
  onSubmit,
  setShowInfoReviewModal,
  travelers,
}: InfoReviewModalProps) => {
  const { t } = useTranslation();
  const getPrefix = (countryId: number) => {
    const country = countries?.find((c: { id: number }) => c.id === countryId);
    return country?.phoneCountryPrefix;
  };

  const formattedPhone = contactInfo.phone.startsWith('0')
    ? contactInfo.phone.slice(1)
    : contactInfo.phone;

  const handleXCloseModalInfoReviewButton = () => {
    const continueButton = document.getElementById('bookNow-button');

    continueButton?.focus();
  };

  const handleModalDismiss = () => {
    setShowInfoReviewModal(false);
    handleXCloseModalInfoReviewButton();
  };

  return (
    <Dialog
      closeButtonAriaLabel={t('ScreenReader.TravelerDetails.InfoReviewModal.CloseButton')}
      data-testid="info-review-dialog"
      footer={
        <Stack spacing={16}>
          <Text>{t('TravelerDetails.InfoReviewModal.AccuracyConfirmation.Text')}</Text>
          <Inline align="end">
            <Button data-testid="info-review-submit-button" onClick={onSubmit} variant="primary">
              {t('TravelerDetails.InfoReviewModal.Button')}
            </Button>
          </Inline>
        </Stack>
      }
      isOpen={isOpen}
      onDismiss={handleModalDismiss}
      title={t('TravelerDetails.InfoReviewModal.Header.Label')}
    >
      <Stack spacing={16}>
        <Text asHtml variant={textVariants._16}>
          {t('TravelerDetails.InfoReviewModal.Text')}
        </Text>
        <div className={dividerStyles} />
        <Stack spacing={8}>
          <Inline className={informationWrapperStyles}>
            <Heading level={3}>
              {t('TravelerDetails.InfoReviewModal.ContactInformation.Heading')}
            </Heading>
            <Text as="p" variant={textVariants._16}>
              <LinkButton
                aria-label={t(
                  'ScreenReader.TravelerDetails.InfoReviewModal.EditContactInfo.Button',
                )}
                className={linkButtonStyles}
                data-testid="info-review-contact-info-link-button"
                onClick={() => handleEditInfo('contactForm-title')}
              >
                {t('TravelerDetails.InfoReviewModal.EditContactInfo.Button')}
              </LinkButton>
            </Text>
          </Inline>
          <Text as="p" variant={textVariants._16}>
            {t('TravelerDetails.InfoReviewModal.ContactInformation.Text')}
          </Text>
          <Inline alignY="center" spacing={8}>
            <Envelope aria-hidden="true" size="20" />
            <Text as="p" variant={textVariants._16_BOLD}>
              {t('TravelerDetails.InfoReviewModal.Email.Label')}
            </Text>
            <Text
              as="p"
              className={MOUSE_FLOW_CLASS_NAMES}
              data-testid="info-review-customer-email"
              variant={textVariants._16}
            >
              {contactInfo.email}
            </Text>
          </Inline>
          <Inline alignY="center" spacing={8}>
            <Phone aria-hidden="true" size="20" />
            <Text as="p" variant={textVariants._16_BOLD}>
              {t('TravelerDetails.InfoReviewModal.Phone.Label')}
            </Text>
            <Text
              as="p"
              className={MOUSE_FLOW_CLASS_NAMES}
              data-testid="info-review-customer-phone"
              variant={textVariants._16}
            >
              {`${phone.addPrefix(getPrefix(contactInfo.phoneCountry))} ${formattedPhone}`}
            </Text>
          </Inline>
        </Stack>
        <div className={dividerStyles} />
        <Stack spacing={8}>
          <Inline className={informationWrapperStyles}>
            <Heading level={3}>
              {t('TravelerDetails.InfoReviewModal.TravelerInformation.Heading')}
            </Heading>
            <Text as="p" variant={textVariants._16}>
              <LinkButton
                aria-label={t(
                  'ScreenReader.TravelerDetails.InfoReviewModal.EditTravelerInfo.Button',
                )}
                className={linkButtonStyles}
                data-testid="info-review-traveler-info-link-button"
                onClick={() => handleEditInfo('traveler-heading-0')}
              >
                {t('TravelerDetails.InfoReviewModal.EditTravelerInfo.Button')}
              </LinkButton>
            </Text>
          </Inline>
          {travelers.map((traveler: Traveler, index: number) => (
            <InfoReviewModalTraveler {...traveler} key={`info-review-modal-traveler-${index}`} />
          ))}
        </Stack>
        <div className={dividerStyles} />
      </Stack>
    </Dialog>
  );
};

export default InfoReviewModal;
